import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, BookOpen, Lock, User, Globe, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function DisciplineDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.discipline(slug)
      .then(d => alive && setData(d))
      .catch(() => alive && toast.error("Discipline not found"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  if (loading) return (
    <div className="animate-pulse space-y-6" data-testid="discipline-loading">
      <div className="h-8 w-40 bg-white/5 rounded" />
      <div className="h-32 bg-white/[0.03] rounded-lg" />
      <div className="grid md:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-32 bg-white/[0.03] rounded-lg" />)}
      </div>
    </div>
  );

  if (!data) return (
    <div className="archival-card rounded-lg p-12 text-center" data-testid="discipline-empty">
      <p className="font-display text-2xl">Discipline not found</p>
      <Link to="/collections" className="btn-ghost-gold inline-flex mt-4 items-center gap-2">
        <ArrowLeft className="w-4 h-4" /> Back to Collections
      </Link>
    </div>
  );

  const live = data.series.filter(s => s.status === "live");
  const coming = data.series.filter(s => s.status !== "live");

  return (
    <div className="animate-fade-up" data-testid="page-discipline">
      <Link to="/collections" className="inline-flex items-center gap-2 text-white/60 hover:text-emerald-soft mb-6" data-testid="discipline-back">
        <ArrowLeft className="w-4 h-4" /> Back to Collections
      </Link>

      <section className="relative overflow-hidden rounded-xl border border-white/8 bg-gradient-to-br from-[#1a1108] via-[#161009] to-[#0F0B08] px-6 lg:px-10 py-10 lg:py-12 mb-12">
        <div className="absolute -top-16 -right-16 w-[380px] h-[380px] rounded-full bg-emerald-glow/[0.08] blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[260px] h-[260px] rounded-full bg-gold/[0.06] blur-3xl" />
        <div className="relative max-w-3xl">
          <p className="label-caps text-gold/85 mb-2">Discipline</p>
          <h1 className="font-display text-4xl lg:text-5xl leading-tight text-paper" data-testid="discipline-title">{data.title}</h1>
          {data.translation && <p className="text-lg text-gold/80 italic mt-2">{data.translation}</p>}
          <p className="text-white/70 mt-4 leading-relaxed">{data.description}</p>
        </div>
      </section>

      {live.length > 0 && (
        <section className="mb-12">
          <div className="flex items-baseline gap-3 mb-6">
            <h2 className="font-display text-2xl text-paper">Available Now</h2>
            <span className="label-caps text-emerald-soft">{live.length} series</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="discipline-live">
            {live.map(s => (
              <Link
                key={s.slug}
                to={`/series/${encodeURIComponent(s.name)}`}
                data-testid={`discipline-live-${s.slug}`}
                className="archival-card featured rounded-lg p-6 group"
              >
                <div className="flex items-start gap-5">
                  {s.thumbnail && (
                    <div className="w-20 h-20 rounded-md overflow-hidden bg-black/40 border border-white/5 shrink-0">
                      <img src={s.thumbnail} alt="" className={`w-full h-full ${s.cover_fit === "contain" ? "object-contain" : "object-cover"}`} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-soft animate-pulse-glow" />
                      <span className="label-caps text-emerald-soft">Live · {s.live_count} lectures</span>
                      {s.year_started && (
                        <span className="label-caps text-gold/70">· {s.year_started}</span>
                      )}
                    </div>
                    <h3 className="font-display text-2xl text-paper group-hover:text-emerald-soft transition-colors">{s.name}</h3>
                    {s.translation && <p className="text-sm text-gold/80 mt-1 italic">{s.translation}</p>}
                    <p className="text-[13px] text-white/60 mt-3 leading-relaxed line-clamp-3">{s.description}</p>
                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/50">
                      {s.author && <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3 text-gold" /> {s.author}</span>}
                      {s.urdu_by && <span className="inline-flex items-center gap-1"><User className="w-3 h-3 text-emerald-soft" /> {s.urdu_by}</span>}
                      {s.language && <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3 text-emerald-soft" /> {s.language}</span>}
                      {s.pdf_url && <span className="inline-flex items-center gap-1 text-gold/85"><FileText className="w-3 h-3" /> PDF available</span>}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {coming.length > 0 && (
        <section>
          <div className="flex items-baseline gap-3 mb-6">
            <h2 className="font-display text-2xl text-paper">Coming Soon</h2>
            <span className="label-caps text-gold/85">{coming.length} series in production</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="discipline-coming">
            {coming.map(s => (
              <div
                key={s.slug}
                data-testid={`discipline-coming-${s.slug}`}
                className="archival-card rounded-lg p-6 opacity-85"
              >
                <div className="flex items-start gap-5">
                  <div className="w-20 h-20 rounded-md bg-gradient-to-br from-gold/10 to-emerald-glow/5 border border-white/5 shrink-0 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-gold/70" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="label-caps text-gold/75">Coming soon · {s.total_planned} lectures planned</span>
                    <h3 className="font-display text-2xl text-paper mt-1">{s.name}</h3>
                    {s.translation && <p className="text-sm text-gold/70 mt-1 italic">{s.translation}</p>}
                    <p className="text-[13px] text-white/55 mt-3 leading-relaxed line-clamp-3">{s.description}</p>
                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/45">
                      {s.author && <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3 text-gold/70" /> {s.author}</span>}
                      {s.urdu_by && <span className="inline-flex items-center gap-1"><User className="w-3 h-3 text-emerald-soft/70" /> {s.urdu_by}</span>}
                      {s.language && <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3 text-emerald-soft/70" /> {s.language}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
